import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t("reset.mismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <img src="/pruva-logo-full.png" alt="Pruva Swimming Pools" className="h-12 w-auto" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Project Tracking</span>
        </div>
        <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">{t("reset.title")}</h2>

        {!token && (
          <div className="mt-6 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="reset-no-token">
            {t("reset.noToken")}
          </div>
        )}
        {done ? (
          <div className="mt-6 space-y-4">
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid="reset-success">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              {t("reset.success")}
            </div>
            <Link to="/login" className="block text-center text-sm text-sky-700 hover:underline" data-testid="reset-goto-login">
              {t("reset.gotoSignIn")}
            </Link>
          </div>
        ) : (
          token && (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              {error && (
                <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="reset-error">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="pw">{t("reset.newPassword")}</Label>
                <Input id="pw" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password-input" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw2">{t("reset.confirmPassword")}</Label>
                <Input id="pw2" type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="reset-submit-button">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("reset.updatePassword")}
              </Button>
            </form>
          )
        )}
      </div>
    </div>
  );
}
